import CvDeliveryHistory from "./CvDeliveryHistory";
import axios from "../axios/AxiosInstance";
import { latestClientComment } from "../commercial/services/latest-client-comment.mjs";
import { Phone, ChevronDown, FileUser } from 'lucide-react';
import CommercialRetention from "./CommercialRetention";
import CommercialObjectives from "./CommercialObjectives";
import LegalCheck from "./LegalCheck";
import PortfolioActions from "./PortfolioActions";
import React, { useEffect, useState } from "react";
import {
  crmV2Indicators,
  whyCall,
  defaultCallRules,
  missionInactivity,
} from "../commercial/services/crm-v2-policy.mjs";
const labels = {
  quoteDays: "Relance devis (jours ouvrés)",
  cvDays: "Relance CV anonymisés (jours ouvrés)",
  brochureDays: "Relance plaquette (jours ouvrés)",
  unansweredDays: "Nouvelle tentative (jours ouvrés)",
  noNeedDays: "Veille commerciale (jours ouvrés)",
  decliningDays: "Activité en baisse (jours ouvrés)",
  maxAttempts: "Tentatives avant recherche de coordonnées",
  futureLeadDays: "Anticipation du besoin (jours ouvrés)",
};
export default function CrmV2Panel({
  state,
  cvPresentations = [],
  view = "all",
  activityContent,
  client,
  api,
  canConfigure = false,
  onRules,
  onReactivate,
  onClientSettings,
}) {
  const [error, setError] = useState(""),
    [open, setOpen] = useState(false),
    [team, setTeam] = useState(null),
    [teamOwner, setTeamOwner] = useState(""),
    [rules, setRules] = useState({
      ...defaultCallRules,
      ...state.automationRules,
    }),
    [templates, setTemplates] = useState(""),
    [exclusions, setExclusions] = useState([]),
    [busy, setBusy] = useState(false),
    [reason, setReason] = useState(""),
    [weights, setWeights] = useState(state.scoreWeights || {});
  const clientHistoryId = client?.client_id || client?._id || client?.id;
  const [commentHistory, setCommentHistory] = useState(null);
  useEffect(() => {
    if (!clientHistoryId || view === "activity") return;
    let active = true;
    axios.get(`newtask/history/client/${encodeURIComponent(clientHistoryId)}`)
      .then(({data}) => { if (active) setCommentHistory({id:clientHistoryId,rows:Array.isArray(data) ? data : []}); })
      .catch(() => { if (active) setCommentHistory({id:clientHistoryId,rows:[],error:true}); });
    return () => { active = false; };
  }, [clientHistoryId, view, state.results, state.tasks]);
  const lastComment = client ? latestClientComment(client, state, commentHistory?.id === clientHistoryId ? commentHistory.rows : []) : null;
  const [cvOpenFor, setCvOpenFor] = useState(null);
  const clientCvCount = new Set(cvPresentations.filter(row => row.status === "sent" && [client?.id, client?.client_id, client?._id].filter(Boolean).some(id => String(id) === String(row.clientId))).map(row => row._id)).size;
  const [legalEnabled, setLegalEnabled] = useState(false);
  const [inactivity, setInactivity] = useState({
    warning: 15,
    reactivation: 30,
    recovery: 60,
    manager: 90,
    ...state.inactivityThresholds,
  });
  const [clientInactivity, setClientInactivity] = useState({
    warning: 15,
    reactivation: 30,
    recovery: 60,
    manager: 90,
    ...state.inactivityThresholds,
    ...client?.inactivityThresholds,
  });
  useEffect(() => {
    setClientInactivity({
      warning: 15,
      reactivation: 30,
      recovery: 60,
      manager: 90,
      ...state.inactivityThresholds,
      ...client?.inactivityThresholds,
    });
  }, [client?.id, client?.inactivityThresholds, state.inactivityThresholds]);
  const reportState = team
    ? {
        ...team,
        ...(teamOwner
          ? {
              clients: team.clients.filter((c) => c.ownerId === teamOwner),
              ...Object.fromEntries(
                [
                  "results",
                  "tasks",
                  "needs",
                  "quotes",
                  "events",
                  "signals",
                ].map((name) => [
                  name,
                  (team[name] || []).filter(
                    (row) =>
                      String(
                        row.ownerId ||
                          team.clients.find((c) => c.id === row.clientId)
                            ?.ownerId,
                      ) === teamOwner,
                  ),
                ]),
              ),
            }
          : {}),
      }
    : state;
  const kpi = crmV2Indicators(reportState),
    prep = client ? whyCall(client, state) : null;
  useEffect(() => {
    let active = true;
    api
      .get("commercial/rules")
      .then(({ data }) => {
        if (active) {
          setRules({ ...defaultCallRules, ...data.automationRules });
          setLegalEnabled(!!data.legalVerificationEnabled);
          setWeights(data.scoreWeights || {});
          setInactivity({
            warning: 15,
            reactivation: 30,
            recovery: 60,
            manager: 90,
            ...data.inactivityThresholds,
          });
          setTemplates((data.noteTemplates || []).join("\n"));
          onRules?.(data);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [api]);
  const loadExclusions = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get("commercial/exclusions");
      setExclusions(data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!canConfigure) return;
    let active = true;
    const refresh = () =>
      api
        .get("commercial/team")
        .then(({ data }) => {
          if (active && Array.isArray(data?.clients)) setTeam(data);
        })
        .catch(() => {});
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [api, canConfigure]);
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("commercial/rules", {
        automationRules: rules,
        inactivityThresholds: inactivity,
        legalVerificationEnabled: legalEnabled,
        scoreWeights: weights,
        noteTemplates: templates.split("\n"),
      });
      onRules?.(data);
      setOpen(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className={`my-4 rounded-xl border border-slate-200 bg-white p-4 space-y-3 ${view === "client" ? "crmCallAssistant" : "crmActivitySummary"}`}
      aria-label={view === "client" ? "Préparer l’appel et vérifier le client" : "Suivi de mon activité"}
    >
      {view !== "client" && <>
      {canConfigure && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="secondary"
            onClick={async () => {
              setError("");
              try {
                const { data } = await api.get("commercial/team");
                setTeam(data);
              } catch (e) {
                setError(e.response?.data?.message || e.message);
              }
            }}
          >
            Actualiser la vue agence
          </button>
          {team && (
            <select
              aria-label="Commercial du rapport"
              value={teamOwner}
              onChange={(event) => setTeamOwner(event.target.value)}
            >
              <option value="">Toute l’agence</option>
              {team.users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      <div className="flex flex-wrap justify-between gap-2">
        <h3 className="font-semibold">Suivi de mon activité</h3>
        {canConfigure && (
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setOpen(!open);
              if (!open) loadExclusions();
            }}
          >
            Règles et exclusions
          </button>
        )}
      </div>
      {activityContent}
      {canConfigure && team && (
        <PortfolioActions api={api} clients={team.clients} users={team.users} />
      )}

      {canConfigure && <CommercialRetention api={api} />}
      </>}
      {view !== "activity" && <>
      {view === "client" && <div className="crmAssistantHeading"><h3>Avant d’appeler</h3><p>{client?.name || "Client sélectionné"}</p></div>}
      {prep && (
        <details className="crmToolCard crmToolCardPrimary">
          <summary><span className="crmToolIcon"><Phone size={18} aria-hidden="true" /></span><span className="crmToolText"><strong>Préparer mon appel</strong><small>Dernier échange et prochaine action</small></span><ChevronDown className="crmToolChevron" size={17} aria-hidden="true" /></summary>
          <div className="crmToolBody space-y-2">
            <strong>{client.name}</strong>
            <p>
              Dernier échange :{" "}
              {prep.lastInteraction
                ? new Date(prep.lastInteraction).toLocaleString("fr-FR")
                : "Aucun échange enregistré"}
            </p>
            <p>Dernier résultat : {prep.lastResult || "À qualifier"}</p>
            {lastComment && <div className="crmLastComment"><strong>Dernier commentaire</strong>
              {lastComment.date && <small>{new Date(lastComment.date).toLocaleString("fr-FR")}</small>}
              <p>{lastComment.text}</p>
            </div>}
            {commentHistory?.id === clientHistoryId && commentHistory.error && <p className="hint">L’historique des commentaires n’a pas pu être chargé.</p>}
            {prep.lastObjection && <p>Objection : {prep.lastObjection}</p>}
            {prep.latestOffer && (
              <p>
                Recrutement : {prep.latestOffer.job} · {prep.latestOffer.date}{" "}
                {prep.latestOffer.sourceUrl && (
                  <a
                    href={prep.latestOffer.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Voir l’annonce
                  </a>
                )}
              </p>
            )}
            <p>
              {prep.opportunities.length} opportunité(s) ouverte(s) ·{" "}
              {prep.attempts} tentative(s)
            </p>
            <strong>Action recommandée : {prep.recommendation}</strong>
          </div>
        </details>
      )}
      {view === "client" && clientCvCount > 0 && <details key={`cv-${clientHistoryId}`} className="crmToolCard">
        <summary onClick={() => setCvOpenFor(clientHistoryId)}><span className="crmToolIcon"><FileUser size={18} aria-hidden="true" /></span><span className="crmToolText"><strong>CV proposés ({clientCvCount})</strong><small>Profils envoyés et retours du client</small></span><ChevronDown className="crmToolChevron" size={17} aria-hidden="true" /></summary>
        <div className="crmToolBody">{cvOpenFor === clientHistoryId && <CvDeliveryHistory compact kind="client" id={clientHistoryId} />}</div>
      </details>}
      <LegalCheck key={client?.id || client?.client_id} api={api} client={client} />
      </>}
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {open && canConfigure && (
        <div className="space-y-4 border-t pt-3">
          <h4 className="font-semibold">Seuils d’inactivité (jours)</h4>
          <p className="text-sm">
            Les seuils doivent être croissants. Les clients saisonniers et les
            besoins futurs identifiés restent protégés.
          </p>
          <div className="grid gap-3 sm:grid-cols-4">
            {Object.entries({
              warning: "Vigilance",
              reactivation: "Réactivation",
              recovery: "Reconquête",
              manager: "Alerte responsable",
            }).map(([key, label]) => (
              <label key={key}>
                {label}
                <input
                  type="number"
                  min="1"
                  max="3650"
                  className="w-full rounded-lg border bg-white p-2"
                  value={inactivity[key]}
                  onChange={(e) =>
                    setInactivity({
                      ...inactivity,
                      [key]: Number(e.target.value),
                    })
                  }
                />
              </label>
            ))}
          </div>
          {client && onClientSettings && (
            <details>
              <summary>Rythme propre à {client.name}</summary>
              <div className="grid gap-3 sm:grid-cols-4">
                {Object.keys(clientInactivity).map((key) => (
                  <label key={key}>
                    {
                      {
                        warning: "Vigilance",
                        reactivation: "Réactivation",
                        recovery: "Reconquête",
                        manager: "Responsable",
                      }[key]
                    }
                    <input
                      type="number"
                      min="1"
                      max="3650"
                      className="w-full rounded-lg border bg-white p-2"
                      value={clientInactivity[key]}
                      onChange={(e) =>
                        setClientInactivity({
                          ...clientInactivity,
                          [key]: Number(e.target.value),
                        })
                      }
                    />
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  const values = Object.values(clientInactivity);
                  if (
                    values.some(
                      (v, i) =>
                        !Number.isInteger(v) ||
                        v < 1 ||
                        v > 3650 ||
                        (i > 0 && v <= values[i - 1]),
                    )
                  ) {
                    setError(
                      "Les seuils doivent être des jours entiers strictement croissants.",
                    );
                    return;
                  }
                  onClientSettings(client.id, clientInactivity);
                }}
              >
                Enregistrer pour ce client
              </button>
            </details>
          )}
          <label className="block">
            <input
              type="checkbox"
              checked={legalEnabled}
              onChange={(e) => setLegalEnabled(e.target.checked)}
            />{" "}
            Consulter automatiquement le BODACC par SIREN pour les entreprises
            enregistrées (contrôle quotidien, sans levée automatique des blocages).
          </label>
          <h4 className="font-semibold">Cadences de relance</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(labels).map(([name, label]) => (
              <label key={name} className="text-sm">
                {label}
                <input
                  type="number"
                  min="1"
                  max={name === "maxAttempts" ? 20 : 365}
                  value={rules[name]}
                  onChange={(event) =>
                    setRules({ ...rules, [name]: Number(event.target.value) })
                  }
                  className="w-full rounded-lg border bg-white p-2"
                />
              </label>
            ))}
          </div>
          <details>
            <summary>Pondération du score commercial (0 à 100)</summary>
            <p className="text-sm">
              Les règles actuelles restent actives tant qu’aucune pondération
              n’est définie.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {Object.entries({
                recentOffer: "Offre récente",
                volume: "Volume",
                recurrence: "Récurrence",
                jobMatch: "Métier couvert",
                location: "Localisation renseignée",
                collaboration: "Collaboration passée",
                positiveReply: "Réponse positive",
                quote: "Devis en cours",
                futureNeed: "Besoin futur",
              }).map(([name, label]) => (
                <label key={name} className="text-sm">
                  {label}
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={weights[name] ?? ""}
                    placeholder="Règle actuelle"
                    onChange={(event) =>
                      setWeights({
                        ...weights,
                        [name]: Number(event.target.value),
                      })
                    }
                    className="w-full rounded-lg border p-2"
                  />
                </label>
              ))}
            </div>
          </details>
          <label className="block text-sm">
            Modèles de notes (un par ligne)
            <textarea
              value={templates}
              onChange={(e) => setTemplates(e.target.value)}
              className="w-full rounded-lg border bg-white p-2"
            />
          </label>
          <button
            disabled={busy}
            type="button"
            className="primary"
            onClick={save}
          >
            Enregistrer les règles
          </button>
          <h4 className="font-semibold">Exclusions centralisées</h4>
          <p className="text-sm">
            Les exclusions bloquent les futurs imports. La levée d’une
            opposition nécessite une vérification spécifique.
          </p>
          <input
            aria-label="Justification de réactivation"
            placeholder="Justification de la correction"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-lg border p-2"
          />
          {exclusions.map((entry) => (
            <div
              key={entry._id}
              className="flex flex-wrap justify-between gap-2 rounded-lg bg-slate-50 p-3 text-sm"
            >
              <span>
                {entry.siret || "SIRET non connu"} · {entry.reason} ·{" "}
                {entry.active ? "Actif" : "Levée"}
              </span>
              {entry.active &&
                entry.reason !== "Opposition à la prospection" && (
                  <button
                    type="button"
                    disabled={busy || !reason.trim()}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await api.post(
                          `commercial/exclusions/${encodeURIComponent(entry._id)}/reactivate`,
                          { reason },
                        );
                        onReactivate?.(entry.clientId);
                        await loadExclusions();
                      } catch (e) {
                        setError(e.response?.data?.message || e.message);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Corriger l’exclusion
                  </button>
                )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
